import React, { useMemo } from 'react';
import { BasePage } from '../../components/base-page';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableHead,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { isValidIBAN, friendlyFormatIBAN, electronicFormatIBAN } from 'ibantools';

import { useAlert } from '@dwelvio/react-alert';
import { useAuth } from '@dwelvio/auth-react';
import { AccountPlanLimitLabels, IUserData, PlanLimit, PlanTypeLabelArray } from '../../types';
import {
  AccountFragment,
  AccountInvitationPayloadFragment,
  AccountMemberFragment,
  ConfirmAccountLogoUploadDocument,
  ConfirmAccountLogoUploadMutation,
  ConfirmAccountLogoUploadMutationVariables,
  DeleteAccountInvitationDocument,
  DeleteAccountInvitationMutation,
  DeleteAccountInvitationMutationVariables,
  GetAccountByIdDocument,
  GetAccountByIdQuery,
  GetAccountByIdQueryVariables,
  GetAccountPlanUsageDocument,
  GetAccountPlanUsageQuery,
  GetAccountPlanUsageQueryVariables,
  InviteUserToAccountDocument,
  InviteUserToAccountMutation,
  InviteUserToAccountMutationVariables,
  RemoveMemberFromAccountDocument,
  RemoveMemberFromAccountMutation,
  RemoveMemberFromAccountMutationVariables,
  SendStatusUpdateDocument,
  SendStatusUpdateMutation,
  SendStatusUpdateMutationVariables,
  StatusUpdateFrequency,
  UpdateAccountDocument,
  UpdateAccountMutation,
  UpdateAccountMutationVariables,
  UploadAccountLogoDocument,
  UploadAccountLogoMutation,
  UploadAccountLogoMutationVariables,
} from '../../generated/graphql';
import { ListWithRemove } from '../../components/list-with-remove';
import { InputInvitationDialog } from './components/input-invitation-dialog';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import { InfoDialog } from '../../components/info-dialog';
import { appPaths } from '../../app-paths';
import { UploadButton } from '../../components/upload-button';

const DOCUMENT_TAG_COLORS = [
  '#1565c0',
  '#2e7d32',
  '#ef6c00',
  '#6a1b9a',
  '#c62828',
  '#00838f',
  '#283593',
  '#ad1457',
  '#5d4037',
  '#455a64',
];
const DOCUMENT_TAG_DEFAULT_COLOR = DOCUMENT_TAG_COLORS[0];

export function AccountPage() {
  const { show } = useAlert();
  const { user } = useAuth<IUserData>();
  const navigate = useNavigate();
  const [showNewInvitation, setShowNewInvitation] = React.useState(false);
  const [showInvitationSuccess, setShowInvitationSuccess] = React.useState<AccountInvitationPayloadFragment | null>(
    null,
  );
  const [showRemoveInvitation, setShowRemoveInvitation] = React.useState<
    AccountFragment['invitations'][0] | undefined
  >();
  const [showRemoveMember, setShowRemoveMember] = React.useState<AccountMemberFragment | undefined>();
  const [accountName, setAccountName] = React.useState('');
  const [bankAccountNumber, setBankAccountNumber] = React.useState('');
  const [ibanError, setIbanError] = React.useState('');
  const [bankAccountName, setBankAccountName] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState('');
  const [logoError, setLogoError] = React.useState(false);
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [nextInvoiceNo, setNextInvoiceNo] = React.useState(1);
  const [autoResetInvoiceNo, setAutoResetInvoiceNo] = React.useState(false);
  const [statusUpdateFrequency, setStatusUpdateFrequency] = React.useState<StatusUpdateFrequency>('none');
  const [sendingStatusUpdate, setSendingStatusUpdate] = React.useState(false);
  const [creditNotification, setCreditNotification] = React.useState(true);
  const [documentTags, setDocumentTags] = React.useState<{ name: string; color: string }[]>([]);
  const [newDocumentTagName, setNewDocumentTagName] = React.useState('');
  const [newDocumentTagColor, setNewDocumentTagColor] = React.useState(DOCUMENT_TAG_DEFAULT_COLOR);

  const [uploadLogo] = useMutation<UploadAccountLogoMutation, UploadAccountLogoMutationVariables>(
    UploadAccountLogoDocument,
  );
  const [confirmLogo] = useMutation<ConfirmAccountLogoUploadMutation, ConfirmAccountLogoUploadMutationVariables>(
    ConfirmAccountLogoUploadDocument,
  );
  const [inviteUser] = useMutation<InviteUserToAccountMutation, InviteUserToAccountMutationVariables>(
    InviteUserToAccountDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa vytvoriť pozvánku.', e.message),
    },
  );
  const [deleteInvitation] = useMutation<DeleteAccountInvitationMutation, DeleteAccountInvitationMutationVariables>(
    DeleteAccountInvitationDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa zmazať pozvánku.', e.message),
    },
  );
  const [removeMember] = useMutation<RemoveMemberFromAccountMutation, RemoveMemberFromAccountMutationVariables>(
    RemoveMemberFromAccountDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa zmazať používateľa.', e.message),
    },
  );
  const [updateAccount] = useMutation<UpdateAccountMutation, UpdateAccountMutationVariables>(UpdateAccountDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa aktualizovať účet.', e.message),
  });
  const [sendStatusUpdate] = useMutation<SendStatusUpdateMutation, SendStatusUpdateMutationVariables>(
    SendStatusUpdateDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa odoslať stavový e-mail.', e.message),
    },
  );

  const { data, loading, error, refetch } = useQuery<GetAccountByIdQuery, GetAccountByIdQueryVariables>(
    GetAccountByIdDocument,
    {
      variables: { id: user?.accountId },
    },
  );

  const {
    data: usageData,
    loading: usageLoading,
    refetch: refetchUsage,
    error: usageError,
  } = useQuery<GetAccountPlanUsageQuery, GetAccountPlanUsageQueryVariables>(GetAccountPlanUsageDocument);

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať účet.', error.message);
    }
  }, [error, show]);

  React.useEffect(() => {
    if (data?.getAccountById) {
      setAccountName(data.getAccountById.name || '');
      // Format IBAN for display (with spaces)
      const bankAccountNoValue = data.getAccountById.bankAccountNo || '';
      setBankAccountNumber(bankAccountNoValue ? friendlyFormatIBAN(bankAccountNoValue) || bankAccountNoValue : '');
      setBankAccountName(data.getAccountById.bankAccountName || '');
      setLogoUrl(data.getAccountById.logoUrl || '');
      setLogoError(false);

      setNextInvoiceNo(data.getAccountById.nextInvoiceNo || 1);
      setAutoResetInvoiceNo(data.getAccountById.autoResetInvoiceNo || false);
      setStatusUpdateFrequency(data.getAccountById.statusUpdateFrequency || 'none');
      setCreditNotification(data.getAccountById.creditNotification ?? true);
      setDocumentTags(
        (data.getAccountById.tags ?? [])
          .filter((tag) => tag.entityType === 'document')
          .map((tag) => ({ name: tag.name, color: normalizeDocumentTagColor(tag.color) })),
      );
    }
  }, [data]);

  const accountId = user?.accountId;
  const account = data?.getAccountById;
  const usage = usageData?.getAccountPlanUsage || [];

  const handleAccountNameSave = () => {
    if (!accountName || accountName.trim() === '') {
      show.error('Názov účtu nemôže byť prázdny.');
      return;
    }
    updateAccount({
      variables: { input: { name: accountName } },
      onCompleted: () => {
        show.info('Názov účtu bol úspešne uložený.');
        refetch();
      },
    });
  };

  const handleBankAccountNumberChange = (value: string) => {
    // Allow user to type with or without spaces
    setBankAccountNumber(value);
    setIbanError('');
  };

  const handleIbanSave = () => {
    // Convert to electronic format (no spaces) for validation and saving
    const electronicIban = bankAccountNumber ? electronicFormatIBAN(bankAccountNumber) || bankAccountNumber : '';

    if (electronicIban && !isValidIBAN(electronicIban)) {
      setIbanError('Neplatný IBAN');
      return;
    }
    setIbanError('');
    updateAccount({
      variables: { input: { bankAccountNo: friendlyFormatIBAN(electronicIban) } },
      onCompleted: () => {
        show.info('Číslo bankového účtu bolo úspešne uložené.');
        refetch();
      },
    });
  };

  const handleBankAccountNameSave = () => {
    updateAccount({
      variables: { input: { bankAccountName } },
      onCompleted: () => {
        show.info('Názov účtu bol úspešne uložený.');
        refetch();
      },
    });
  };

  const handleNextInvoiceNoSave = () => {
    if (nextInvoiceNo < 1) {
      show.error('Číslo ďalšej faktúry musí byť väčšie ako 0.');
      return;
    }
    updateAccount({
      variables: { input: { nextInvoiceNo } },
      onCompleted: () => {
        show.info('Číslo ďalšej faktúry bolo úspešne uložené.');
        refetch();
      },
    });
  };

  const handleAutoResetToggle = () => {
    const newValue = !autoResetInvoiceNo;
    updateAccount({
      variables: { input: { autoResetInvoiceNo: newValue } },
      onCompleted: () => {
        show.info(`Automatické resetovanie ${newValue ? 'zapnuté' : 'vypnuté'}.`);
        refetch();
      },
    });
  };

  const handleStatusUpdateFrequencyChange = (value: 'none' | 'daily' | 'weekly') => {
    updateAccount({
      variables: { input: { statusUpdateFrequency: value } },
      onCompleted: () => {
        setStatusUpdateFrequency(value);
        const labels = { none: 'vypnuté', daily: 'denné', weekly: 'týždenné' };
        show.info(`Stavové e-maily nastavené na: ${labels[value]}.`);
        refetch();
      },
    });
  };

  const handleSendStatusUpdate = async () => {
    setSendingStatusUpdate(true);
    try {
      await sendStatusUpdate();
      show.info('Stavový e-mail bol úspešne odoslaný.');
    } catch (error) {
      // Error is already handled by the mutation's onError
    } finally {
      setSendingStatusUpdate(false);
    }
  };

  const handleCreditTransactionNotificationsToggle = () => {
    const newValue = !creditNotification;
    updateAccount({
      variables: { input: { creditNotification: newValue } },
      onCompleted: () => {
        setCreditNotification(newValue);
        show.info(`Notifikácie o platbách ${newValue ? 'zapnuté' : 'vypnuté'}.`);
        refetch();
      },
    });
  };

  const handleAddDocumentTag = () => {
    const name = newDocumentTagName.trim();
    if (!name) return;
    if (name.length > 20) {
      show.error('Tag môže mať maximálne 20 znakov.');
      return;
    }
    if (documentTags.some((tag) => tag.name.toLocaleLowerCase() === name.toLocaleLowerCase())) {
      show.error('Takýto tag už existuje.');
      return;
    }
    setDocumentTags((prev) => [...prev, { name, color: newDocumentTagColor }]);
    setNewDocumentTagName('');
  };

  const handleSaveDocumentTags = () => {
    updateAccount({
      variables: {
        input: {
          tags: documentTags.map((tag) => ({
            entityType: 'document',
            name: tag.name,
            color: tag.color || null,
          })),
        },
      },
      onCompleted: () => {
        show.info('Tagy dokumentov boli úspešne uložené.');
        refetch();
      },
    });
  };

  const handleLogoUpload = async (file: File) => {
    if (!file) return;

    // Maximum file size (must match server constant MAX_LOGO_SIZE_BYTES in server/src/lib/constants.ts)
    const MAX_LOGO_SIZE = 50 * 1024; // 50KB

    // Validate file size
    if (file.size > MAX_LOGO_SIZE) {
      show.error('Súbor je príliš veľký. Maximálna veľkosť je 50KB.');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      show.error('Súbor musí byť obrázok.');
      return;
    }

    setLogoUploading(true);
    setLogoError(false);

    try {
      // Step 1: Get upload URL
      const uploadResult = await uploadLogo({
        variables: {
          input: {
            filename: file.name,
            size: file.size,
            mimeType: file.type,
          },
        },
      });

      if (!uploadResult.data) {
        throw new Error('Nepodarilo sa získať upload URL');
      }

      const { uploadUrl, logoId } = uploadResult.data.uploadAccountLogo;

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Nepodarilo sa nahrať súbor');
      }

      // Step 3: Confirm upload
      await confirmLogo({
        variables: {
          logoId,
          size: file.size,
        },
      });

      show.info('Logo bolo úspešne nahrané.');
      await refetch();
    } catch (error) {
      show.error('Chyba pri nahrávaní loga.');
      console.error(error);
    } finally {
      setLogoUploading(false);
    }
  };

  const activeAccount = useMemo(() => user?.accounts.find((a) => a.id === accountId), [user, accountId]);

  return (
    <BasePage title="Účet" loading={loading} errorMsg={account ? undefined : 'Nepodarilo sa načítať účet.'}>
      <Stack direction="column" spacing={2} maxWidth={600} sx={{ marginBottom: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Názov účtu"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            helperText="Názov vášho účtu, ktorý sa používa na identifikáciu vašich nehnuteľností a faktúr"
            fullWidth
            placeholder="Názov účtu"
          />
          <Button variant="contained" onClick={handleAccountNameSave} sx={{ marginTop: 1 }}>
            Uložiť
          </Button>
        </Stack>
        <Typography variant="h3">
          Plán '{PlanTypeLabelArray.find((p) => p.value === activeAccount?.plan)?.label}'
        </Typography>
        {usageError && <Typography color="error">Chyba pri načítaní využitia plánu: {usageError.message}</Typography>}
        {!usageLoading && (
          <Table>
            <TableHead>
              <tr>
                <td align="left" style={{ width: '250px' }}>
                  Atribút
                </td>
                <td align="center" style={{ width: '100px' }}>
                  Využitie
                </td>
                <td align="center" style={{ width: '100px' }}>
                  Limit plánu
                </td>
              </tr>
            </TableHead>
            <TableBody>
              {usage.map((u) => {
                let current = u.current;
                let limit = u.limit;
                if (u.limitName === 'storage') {
                  // Display storage in MB
                  current = u.current;
                  limit = u.limit === -1 ? -1 : u.limit;
                }
                return (
                  <tr key={u.limitName}>
                    <td align="left">{AccountPlanLimitLabels[u.limitName as PlanLimit]}</td>
                    <td align="center">{current}</td>
                    <td align="center">{limit === -1 ? 'Neobmedzené' : limit}</td>
                  </tr>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Nastavenia bankového účtu na prijímanie platieb
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Názov účtu"
            value={bankAccountName}
            onChange={(e) => setBankAccountName(e.target.value)}
            helperText="Meno/názov príjemcu platby, ktoré sa zakomponuje do QR kódu na faktúrach"
            fullWidth
            placeholder="Meno a priezvisko"
          />
          <Button variant="contained" onClick={handleBankAccountNameSave} sx={{ marginTop: 1 }}>
            Uložiť
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Číslo bankového účtu (IBAN)"
            value={bankAccountNumber}
            onChange={(e) => handleBankAccountNumberChange(e.target.value)}
            error={!!ibanError}
            helperText={ibanError || 'Číslo účtu sa použije na generovanie QR kódov vo faktúrach. Formátujte ako IBAN'}
            fullWidth
            placeholder="SK## #### #### #### #### ####"
          />
          <Button variant="contained" onClick={handleIbanSave} sx={{ marginTop: 1 }}>
            Uložiť
          </Button>
        </Stack>
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Logo prenajímateľa
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Typography variant="body2">
          Nahrajte logo (max. 50KB, len obrázky). Logo sa zobrazí v emailoch zasielaných nájomcom.
        </Typography>
        <UploadButton
          label={logoUploading ? 'Nahrávam...' : 'Nahrať logo'}
          onUpload={handleLogoUpload}
          inProgress={logoUploading}
          variant="outlined"
        />
        {logoUrl && !logoError && (
          <Box>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              Aktuálne logo:
            </Typography>
            <img
              src={logoUrl}
              alt="Logo náhľad"
              style={{ maxWidth: '150px', maxHeight: '150px' }}
              onError={() => setLogoError(true)}
            />
          </Box>
        )}
        {logoUrl && logoError && (
          <Typography variant="body2" color="error">
            Nepodarilo sa načítať obrázok.
          </Typography>
        )}
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Nastavenia faktúr
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Typography variant="body2">
          Tu môžete nastaviť formát číslovania faktúr a pripomienky o nezaplatených faktúrach.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Číslo ďalšej faktúry"
            type="number"
            value={nextInvoiceNo}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setNextInvoiceNo(1);
              } else {
                const parsed = parseInt(value, 10);
                if (!isNaN(parsed) && parsed >= 1) {
                  setNextInvoiceNo(parsed);
                }
              }
            }}
            helperText="Číslo, ktoré bude použité pre ďalšiu vytvorenú faktúru"
            fullWidth
            inputProps={{ min: 1 }}
          />
          <Button variant="contained" onClick={handleNextInvoiceNoSave} sx={{ marginTop: 1 }}>
            Uložiť
          </Button>
        </Stack>
        <FormControlLabel
          control={<Checkbox checked={autoResetInvoiceNo} onChange={handleAutoResetToggle} />}
          label="Automaticky resetovať číslo faktúry každý rok 1. januára na YY0001 (kde YY sú posledné 2 cifry roka)"
        />
        <Button
          variant="outlined"
          onClick={() => navigate(appPaths.notificationSettings.navigate())}
          sx={{ alignSelf: 'flex-start' }}
        >
          Nastaviť pripomienky faktúr
        </Button>
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Stavové e-maily pre prenajímateľov
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Typography variant="body2">
          Nastavte frekvenciu posielania súhrnných e-mailov s prehľadom faktúr, úloh a zmlúv pre používateľov s rolou
          manažér alebo vlastník.
        </Typography>
        <FormControl component="fieldset">
          <FormLabel component="legend">Frekvencia</FormLabel>
          <RadioGroup
            value={statusUpdateFrequency}
            onChange={(e) => handleStatusUpdateFrequencyChange(e.target.value as 'none' | 'daily' | 'weekly')}
          >
            <FormControlLabel value="none" control={<Radio />} label="Neposielať (vypnuté)" />
            <FormControlLabel value="daily" control={<Radio />} label="Denne" />
            <FormControlLabel value="weekly" control={<Radio />} label="Týždenne (každý pondelok)" />
          </RadioGroup>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Stavové e-maily obsahujú:
        </Typography>
        <ul style={{ marginTop: 0, paddingLeft: '20px' }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              Faktúry splatné alebo po splatnosti (nasledujúcich 10 dní)
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              Úlohy s termínom v nasledujúcich 10 dňoch
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              Zmluvy (zvýraznené tie, ktoré končia o 90 dní)
            </Typography>
          </li>
        </ul>
        <Button
          variant="outlined"
          onClick={handleSendStatusUpdate}
          disabled={sendingStatusUpdate}
          sx={{ alignSelf: 'flex-start' }}
        >
          {sendingStatusUpdate ? 'Odosielam...' : 'Odoslať statusový e-mail teraz'}
        </Button>
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Notifikácie o prijatých platbách
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Typography variant="body2">
          Nastavte, či chcete dostávať e-mailové notifikácie pri prijatí platby na váš účet (CREDIT transakcie z
          bankovej pošty).
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={creditNotification} onChange={handleCreditTransactionNotificationsToggle} />}
          label="Posielať notifikácie o prijatých platbách (odporúčané)"
        />
        <Typography variant="body2" color="text.secondary">
          Notifikácie budú odoslané všetkým manažérom a vlastníkom účtu.
        </Typography>
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        Tagy dokumentov
      </Typography>
      <Stack direction="column" spacing={2} maxWidth={600}>
        <Typography variant="body2">
          Vytvorte tagy dokumentov (max. 20 znakov) a nastavte im farbu. Tieto tagy môžete priraďovať dokumentom.
        </Typography>
        {documentTags.map((tag, index) => (
          <Stack key={`${tag.name}-${index}`} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Tag"
              value={tag.name}
              inputProps={{ maxLength: 20 }}
              onChange={(e) => {
                const nextName = e.target.value.slice(0, 20);
                setDocumentTags((prev) => prev.map((item, i) => (i === index ? { ...item, name: nextName } : item)));
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="Farba"
              select
              value={tag.color}
              onChange={(e) => {
                const color = e.target.value;
                setDocumentTags((prev) => prev.map((item, i) => (i === index ? { ...item, color } : item)));
              }}
              sx={{ width: 110 }}
            >
              {DOCUMENT_TAG_COLORS.map((color) => (
                <MenuItem key={color} value={color}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      component="span"
                      sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}
                    />
                    <Box component="span">{color}</Box>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <Button color="error" onClick={() => setDocumentTags((prev) => prev.filter((_, i) => i !== index))}>
              Zmazať
            </Button>
          </Stack>
        ))}
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            label="Nový tag"
            value={newDocumentTagName}
            inputProps={{ maxLength: 20 }}
            onChange={(e) => setNewDocumentTagName(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Farba"
            select
            value={newDocumentTagColor}
            onChange={(e) => setNewDocumentTagColor(e.target.value)}
            sx={{ width: 110 }}
          >
            {DOCUMENT_TAG_COLORS.map((color) => (
              <MenuItem key={color} value={color}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    component="span"
                    sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}
                  />
                  <Box component="span">{color}</Box>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={handleAddDocumentTag}>
            Pridať
          </Button>
        </Stack>
        <Button variant="contained" onClick={handleSaveDocumentTags} sx={{ alignSelf: 'flex-start' }}>
          Uložiť tagy
        </Button>
      </Stack>

      <Typography variant="h3" sx={{ marginTop: 3 }}>
        K tomuto účtu má prístup
      </Typography>
      <ListWithRemove<AccountMemberFragment>
        items={account?.members as AccountMemberFragment[] | undefined}
        keyFn={(i) => i.userId}
        labelFn={(i) => <Item username={i.member.email} roles={i.roles} />}
        onRemove={(i) => setShowRemoveMember(i)}
      />
      <Typography variant="h3">Aktívne pozvánky</Typography>
      <ListWithRemove<AccountFragment['invitations'][0]>
        items={account?.invitations as AccountFragment['invitations'] | undefined}
        keyFn={(i) => i.id}
        labelFn={(i) => <Item username={i.email} roles={i.roles} />}
        onRemove={(i) => setShowRemoveInvitation(i)}
      />
      <Button variant="outlined" onClick={() => setShowNewInvitation(true)} sx={{ marginTop: '20px' }}>
        Pozvať nového používateľa
      </Button>
      {showNewInvitation && (
        <InputInvitationDialog
          open={showNewInvitation}
          onClose={() => setShowNewInvitation(false)}
          onSubmit={(data) => {
            user?.accountId &&
              inviteUser({
                variables: { accountId: user.accountId, input: data },
                onCompleted: (data) => setShowInvitationSuccess(data.inviteUserToAccount),
              });
            setShowNewInvitation(false);
          }}
        />
      )}
      <ConfirmationDialog
        open={!!showRemoveMember}
        onCancel={() => setShowRemoveMember(undefined)}
        onConfirm={() => {
          showRemoveMember &&
            user?.accountId &&
            removeMember({ variables: { accountId: user?.accountId, userId: showRemoveMember.userId } });
          setShowRemoveMember(undefined);
        }}
        title="Zrušenie prístupu"
        message={`Naozaj chcete zrušiť používateľovi '${showRemoveMember?.member.email}' prístup k tomuto účtu?`}
      />
      <ConfirmationDialog
        open={!!showRemoveInvitation}
        onCancel={() => setShowRemoveInvitation(undefined)}
        onConfirm={() => {
          showRemoveInvitation &&
            user?.accountId &&
            deleteInvitation({ variables: { accountId: user?.accountId, invitationId: showRemoveInvitation.id } });
          setShowRemoveInvitation(undefined);
        }}
        title="Zrušenie pozvánky"
        message={`Naozaj chcete zrušiť pozvánku pre '${showRemoveInvitation?.email}'?`}
      />
      {showInvitationSuccess && (
        <InfoDialog
          open={!!showInvitationSuccess}
          onClose={() => setShowInvitationSuccess(null)}
          title="Pozvánka vytvorená"
        >
          <Box width="100%" display="flex" flexDirection="column" gap={2} sx={{ overflowWrap: 'anywhere' }}>
            <Typography variant="body1">
              Pozvánka bola odoslaná na email pozvaného používateľa. Prípadne ju môžete poslať inou formou odoslaním
              toho odkazu <br />
              <br />
              <Link href={showInvitationSuccess.invitationLink ?? '#'}>{showInvitationSuccess.invitationLink}</Link>
            </Typography>
          </Box>
        </InfoDialog>
      )}
    </BasePage>
  );
}

function normalizeDocumentTagColor(color?: string | null): string {
  const normalized = color?.toLocaleLowerCase();
  return normalized && DOCUMENT_TAG_COLORS.includes(normalized) ? normalized : DOCUMENT_TAG_DEFAULT_COLOR;
}

function Item({ username, roles }: { username: string; roles: string[] }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      justifyItems="space-between"
      flexGrow={1}
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <Typography variant="body1" sx={{ flexGrow: 1, width: '150px' }}>
        {username}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        {roles.join(',')}
      </Typography>
    </Stack>
  );
}
